/**
 * Staff console: Quixo expert review desk (track T10). Routes in ./index.tsx (`auditExpertRoutes`).
 *   ExpertQueuePage    reports awaiting review / after review, all tenants
 *   ExpertReviewPage   one report: findings with notes (internal vs customer-visible), severity override, sign-off, threads
 *   ExpertThreadPage   one advisor thread, expert reply
 *   ExpertBookingsPage expert-call bookings: assign, confirm a slot, complete, cancel, waive payment
 * Every call is audited server-side; the UI hides actions a role cannot take, the server still refuses them.
 */
import { useMemo, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { Button, Heading, Text } from '@qm/ui';
import { useSurface } from '../kit/context';
import { useAction, useResource } from '../kit/resource';
import { Badge, Blade, DataTable, ErrorNote, Field, Loadable, Money, Notice, ReasonForm, Screen, Tabs, When } from '../kit/ui';
import {
  bookingActionsFor, canSignOff, canWriteNotes, effectiveSeverity, expertAdminApi, noteValid, reviewTone, signoffProblems,
  type BookingStatus, type QueueStatus, type ReviewJob, type StaffBooking,
} from './api';
import './audit-expert.css';

function useApi() {
  const { http } = useSurface();
  return useMemo(() => expertAdminApi(http), [http]);
}

const Q_TABS: readonly QueueStatus[] = ['awaiting', 'changes_requested', 'approved', 'all'];
export const queueKey = (s: QueueStatus) => `audit-expert:queue:${s}`;
export const jobKey = (t: string, j: string) => `audit-expert:job:${t}:${j}`;
export const threadsKey = (t: string, j: string) => `audit-expert:threads:${t}:${j}`;
export const threadKey = (t: string, id: string) => `audit-expert:thread:${t}:${id}`;
export const bookingsKey = (s: BookingStatus | 'all') => `audit-expert:bookings:${s}`;

export function ExpertQueuePage() {
  const { t } = useSurface();
  const api = useApi();
  const [tab, setTab] = useState<QueueStatus>('awaiting');
  const list = useResource(queueKey(tab), () => api.queue(tab));
  return (
    <Screen title={t('audit-remediation.expert.admin.queue.title')} subtitle={t('audit-remediation.expert.admin.queue.subtitle')} testId="audit-expert-queue"
      actions={<Link className="qm-button" data-variant="secondary" to="/audit-expert/bookings">{t('audit-remediation.expert.admin.bookings.title')}</Link>}>
      <Tabs tabs={Q_TABS.map((id) => ({ id, label: t(`audit-remediation.expert.admin.queue.tab.${id}`) }))} active={tab} onSelect={(id) => setTab(id as QueueStatus)} label={t('audit-remediation.expert.admin.queue.tabsLabel')}>
        <Loadable resource={list}>
          {(rows) => (
            <DataTable rows={rows} rowKey={(r) => `${r.tenantId}:${r.jobId}`} empty={t('audit-remediation.expert.admin.queue.empty')} caption={t('audit-remediation.expert.admin.queue.title')} columns={[
              { key: 'b', header: t('audit-remediation.expert.admin.col.business'), render: (r) => <Link to={`/audit-expert/${encodeURIComponent(r.tenantId)}/${encodeURIComponent(r.jobId)}`}>{r.businessName || r.jobId}</Link> },
              { key: 'd', header: t('audit-remediation.expert.admin.col.domain'), narrow: 'hide', render: (r) => <span dir="ltr">{r.domain ?? '-'}</span> },
              { key: 'tier', header: t('audit-remediation.expert.admin.col.tier'), render: (r) => t(`audit-remediation.expert.tier.${r.tier}`) + (r.paidReview ? ` · ${t('audit-remediation.expert.admin.paidReview')}` : '') },
              { key: 'n', header: t('audit-remediation.expert.admin.col.findings'), narrow: 'hide', render: (r) => r.findingCount },
              { key: 'g', header: t('audit-remediation.expert.admin.col.generated'), narrow: 'hide', render: (r) => <When iso={r.generatedAt} /> },
              { key: 's', header: t('audit-remediation.expert.admin.col.status'), render: (r) => <Badge tone={reviewTone(r.status)}>{t(`audit-remediation.expert.status.${r.status}`)}</Badge> },
            ]} />
          )}
        </Loadable>
      </Tabs>
    </Screen>
  );
}

export function ExpertReviewPage() {
  const { t, roles } = useSurface();
  const api = useApi();
  const { tenantId = '', jobId = '' } = useParams();
  const job = useResource(jobKey(tenantId, jobId), () => api.job(tenantId, jobId));
  return (
    <Screen title={t('audit-remediation.expert.admin.review.title')} testId="audit-expert-review"
      actions={<Link to="/audit-expert">{t('audit-remediation.expert.admin.back')}</Link>}>
      <Loadable resource={job}>
        {(data) => (
          <>
            <div className="qa-expert__summary">
              <Text>{data.report.subject.businessName} · <span dir="ltr">{data.report.subject.domain ?? ''}</span></Text>
              <Badge tone={reviewTone(data.review.status)} testId="review-status">{t(`audit-remediation.expert.status.${data.review.status}`)}</Badge>
              <Text tone="muted">{t(`audit-remediation.expert.tier.${data.tier}`)} · {t(data.review.included ? 'audit-remediation.expert.admin.included' : 'audit-remediation.expert.admin.addon')}</Text>
            </div>
            {!data.review.included && data.review.purchase?.state !== 'paid' && canSignOff(roles) ? <GrantReview tenantId={tenantId} jobId={jobId} onDone={job.reload} /> : null}
            <Heading level={2}>{t('audit-remediation.expert.admin.review.findings')}</Heading>
            <ol className="qa-expert__findings">
              {data.report.findings.map((f) => <FindingReview key={f.id} data={data} findingId={f.id} tenantId={tenantId} jobId={jobId} onChange={job.reload} />)}
            </ol>
            <Heading level={2}>{t('audit-remediation.expert.admin.review.generalNotes')}</Heading>
            <NoteList data={data} findingId={null} tenantId={tenantId} jobId={jobId} onChange={job.reload} />
            <Heading level={2}>{t('audit-remediation.expert.admin.signoff.title')}</Heading>
            {data.signoffs.length ? (
              <ul className="qa-expert__signoffs">
                {data.signoffs.map((s) => (
                  <li key={s.id}>
                    <Badge tone={s.decision === 'approved' ? 'good' : 'warn'}>{t(`audit-remediation.expert.admin.signoff.${s.decision}`)}</Badge>{' '}
                    {t('audit-remediation.expert.admin.signoff.by', { name: s.reviewerName })} <When iso={s.signedAt} />
                    <Text>{s.summary.en}</Text>
                  </li>
                ))}
              </ul>
            ) : <Text tone="muted">{t('audit-remediation.expert.admin.signoff.none')}</Text>}
            {canSignOff(roles) && (data.review.included || data.review.purchase?.state === 'paid') ? <SignoffForm tenantId={tenantId} jobId={jobId} onDone={job.reload} /> : null}
            <ThreadList tenantId={tenantId} jobId={jobId} />
          </>
        )}
      </Loadable>
    </Screen>
  );
}

function FindingReview({ data, findingId, tenantId, jobId, onChange }: { data: ReviewJob; findingId: string; tenantId: string; jobId: string; onChange: () => void }) {
  const { t, roles, locale } = useSurface();
  const f = data.report.findings.find((x) => x.id === findingId)!;
  const sev = effectiveSeverity(f, data.severityOverrides);
  const [editing, setEditing] = useState(false);
  return (
    <li className="qa-expert__finding" data-finding={f.id}>
      <div className="qa-expert__fhead">
        <code dir="ltr">{f.id}</code>
        <strong dir="auto">{locale === 'ar' && f.title.ar ? f.title.ar : f.title.en}</strong>
        <Badge tone={sev.overridden ? 'accent' : 'neutral'} testId={`sev-${f.id}`}>{t(`audit-remediation.severity.${sev.severity}`)}{sev.overridden ? ` (${t('audit-remediation.expert.admin.overridden')})` : ''}</Badge>
      </div>
      <details>
        <summary>{t('audit-remediation.expert.admin.review.evidence')}</summary>
        <Text dir="auto">{f.evidence.en}</Text>
        <ul>{f.evidenceItems.map((e, i) => <li key={i} dir="ltr"><code>{e.checkId}</code> {e.pages.join(', ')} {e.quote ? `"${e.quote}"` : ''}</li>)}</ul>
      </details>
      <NoteList data={data} findingId={f.id} tenantId={tenantId} jobId={jobId} onChange={onChange} />
      {canSignOff(roles) ? (
        editing ? <SeverityForm tenantId={tenantId} jobId={jobId} findingId={f.id} current={sev.severity} onDone={() => { setEditing(false); onChange(); }} />
          : <Button variant="ghost" onClick={() => setEditing(true)} aria-label={t('audit-remediation.expert.admin.severity.changeFor', { id: f.id })}>{t('audit-remediation.expert.admin.severity.change')}</Button>
      ) : null}
    </li>
  );
}

function NoteList({ data, findingId, tenantId, jobId, onChange }: { data: ReviewJob; findingId: string | null; tenantId: string; jobId: string; onChange: () => void }) {
  const { t, roles } = useSurface();
  const api = useApi();
  const notes = data.notes.filter((n) => n.findingId === findingId);
  const flip = useAction((id: string, visibility: 'internal' | 'customer') => api.updateNote(tenantId, jobId, id, { visibility }));
  return (
    <div className="qa-expert__notes">
      {notes.length ? (
        <ul>
          {notes.map((n) => (
            <li key={n.id} data-visibility={n.visibility}>
              <Badge tone={n.visibility === 'customer' ? 'accent' : 'neutral'}>{t(`audit-remediation.expert.admin.visibility.${n.visibility}`)}</Badge>{' '}
              <span dir="auto">{n.body}</span> <When iso={n.updatedAt} />
              {canWriteNotes(roles) ? (
                <Button variant="ghost" size="sm" disabled={flip.pending} onClick={async () => { if (await flip.run(n.id, n.visibility === 'customer' ? 'internal' : 'customer')) onChange(); }}>
                  {t(n.visibility === 'customer' ? 'audit-remediation.expert.admin.notes.makeInternal' : 'audit-remediation.expert.admin.notes.makeVisible')}
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      <ErrorNote error={flip.error} />
      {canWriteNotes(roles) ? <NoteForm tenantId={tenantId} jobId={jobId} findingId={findingId} onDone={onChange} /> : null}
    </div>
  );
}

function NoteForm({ tenantId, jobId, findingId, onDone }: { tenantId: string; jobId: string; findingId: string | null; onDone: () => void }) {
  const { t } = useSurface();
  const api = useApi();
  const [body, setBody] = useState('');
  const [visibility, setVisibility] = useState<'internal' | 'customer'>('internal');
  const add = useAction(() => api.addNote(tenantId, jobId, { findingId, body: body.trim(), visibility }));
  const submit = async (e: FormEvent) => { e.preventDefault(); if (noteValid(body) && (await add.run())) { setBody(''); onDone(); } };
  return (
    <form className="qa-expert__form" onSubmit={submit}>
      <Field label={findingId ? t('audit-remediation.expert.admin.notes.addFor', { id: findingId }) : t('audit-remediation.expert.admin.notes.add')}>
        {(id) => <textarea id={id} name="note" rows={2} maxLength={5000} value={body} onChange={(e) => setBody(e.target.value)} />}
      </Field>
      <Field label={t('audit-remediation.expert.admin.notes.visibility')} hint={t('audit-remediation.expert.admin.notes.visibilityHint')}>
        {(id) => (
          <select id={id} name="visibility" value={visibility} onChange={(e) => setVisibility(e.target.value as 'internal' | 'customer')}>
            <option value="internal">{t('audit-remediation.expert.admin.visibility.internal')}</option>
            <option value="customer">{t('audit-remediation.expert.admin.visibility.customer')}</option>
          </select>
        )}
      </Field>
      <Button type="submit" disabled={!noteValid(body) || add.pending}>{t('audit-remediation.expert.admin.notes.save')}</Button>
      <ErrorNote error={add.error} />
    </form>
  );
}

const SEVERITIES = ['critical', 'high', 'medium', 'low'] as const;

function SeverityForm({ tenantId, jobId, findingId, current, onDone }: { tenantId: string; jobId: string; findingId: string; current: string; onDone: () => void }) {
  const { t } = useSurface();
  const api = useApi();
  const [severity, setSeverity] = useState(SEVERITIES.find((s) => s !== current) ?? 'low');
  const save = useAction((reason: string) => api.overrideSeverity(tenantId, jobId, findingId, { severity, reason }));
  return (
    <div className="qa-expert__form">
      <Field label={t('audit-remediation.expert.admin.severity.new')}>
        {(id) => (
          <select id={id} name="severity" value={severity} onChange={(e) => setSeverity(e.target.value as (typeof SEVERITIES)[number])}>
            {SEVERITIES.map((s) => <option key={s} value={s} disabled={s === current}>{t(`audit-remediation.severity.${s}`)}</option>)}
          </select>
        )}
      </Field>
      <ReasonForm label={t('audit-remediation.expert.admin.severity.reason')} submitLabel={t('audit-remediation.expert.admin.severity.save')} pending={save.pending}
        onSubmit={async (reason) => { if (await save.run(reason)) onDone(); }} />
      <ErrorNote error={save.error} />
    </div>
  );
}

function SignoffForm({ tenantId, jobId, onDone }: { tenantId: string; jobId: string; onDone: () => void }) {
  const { t } = useSurface();
  const api = useApi();
  const [decision, setDecision] = useState<'approved' | 'changes_requested'>('approved');
  const [reviewerName, setName] = useState('');
  const [summaryEn, setEn] = useState('');
  const [summaryAr, setAr] = useState('');
  const [problems, setProblems] = useState<ReturnType<typeof signoffProblems>>({});
  const sign = useAction(() => api.signoff(tenantId, jobId, { decision, reviewerName: reviewerName.trim(), summary: { en: summaryEn.trim(), ar: summaryAr.trim() || null } }));
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const p = signoffProblems({ reviewerName, summaryEn });
    setProblems(p);
    if (p.reviewerName || p.summaryEn) return;
    if (await sign.run()) onDone();
  };
  return (
    <form className="qa-expert__form" onSubmit={submit} data-testid="signoff-form">
      <Field label={t('audit-remediation.expert.admin.signoff.decision')}>
        {(id) => (
          <select id={id} name="decision" value={decision} onChange={(e) => setDecision(e.target.value as typeof decision)}>
            <option value="approved">{t('audit-remediation.expert.admin.signoff.approved')}</option>
            <option value="changes_requested">{t('audit-remediation.expert.admin.signoff.changes_requested')}</option>
          </select>
        )}
      </Field>
      <Field label={t('audit-remediation.expert.admin.signoff.name')} hint={t('audit-remediation.expert.admin.signoff.nameHint')} error={problems.reviewerName ? t(problems.reviewerName) : null}>
        {(id) => <input id={id} name="reviewerName" maxLength={80} value={reviewerName} onChange={(e) => setName(e.target.value)} />}
      </Field>
      <Field label={t('audit-remediation.expert.admin.signoff.summaryEn')} error={problems.summaryEn ? t(problems.summaryEn) : null}>
        {(id) => <textarea id={id} name="summaryEn" rows={3} maxLength={2000} value={summaryEn} onChange={(e) => setEn(e.target.value)} />}
      </Field>
      <Field label={t('audit-remediation.expert.admin.signoff.summaryAr')}>
        {(id) => <textarea id={id} name="summaryAr" rows={3} maxLength={2000} dir="rtl" lang="ar" value={summaryAr} onChange={(e) => setAr(e.target.value)} />}
      </Field>
      <Button type="submit" variant="primary" disabled={sign.pending}>{t('audit-remediation.expert.admin.signoff.submit')}</Button>
      <ErrorNote error={sign.error} />
    </form>
  );
}

function GrantReview({ tenantId, jobId, onDone }: { tenantId: string; jobId: string; onDone: () => void }) {
  const { t } = useSurface();
  const api = useApi();
  const grant = useAction((reason: string) => api.grantReview(tenantId, jobId, reason));
  return (
    <Notice tone="warn" testId="grant-review">
      <Text>{t('audit-remediation.expert.admin.grant.explain')}</Text>
      <ReasonForm label={t('audit-remediation.expert.admin.grant.reason')} submitLabel={t('audit-remediation.expert.admin.grant.submit')} pending={grant.pending}
        onSubmit={async (reason) => { if (await grant.run(reason)) onDone(); }} />
      <ErrorNote error={grant.error} />
    </Notice>
  );
}

function ThreadList({ tenantId, jobId }: { tenantId: string; jobId: string }) {
  const { t } = useSurface();
  const api = useApi();
  const threads = useResource(threadsKey(tenantId, jobId), () => api.threads(tenantId, jobId));
  return (
    <>
      <Heading level={2}>{t('audit-remediation.expert.admin.threads.title')}</Heading>
      <Loadable resource={threads}>
        {(rows) => (
          <DataTable rows={rows} rowKey={(r) => r.id} empty={t('audit-remediation.expert.admin.threads.empty')} columns={[
            { key: 'f', header: t('audit-remediation.expert.admin.threads.finding'), render: (r) => <Link to={`/audit-expert/${encodeURIComponent(tenantId)}/threads/${encodeURIComponent(r.id)}`}><span dir="auto">{r.title}</span></Link> },
            { key: 'n', header: t('audit-remediation.expert.admin.threads.messages'), render: (r) => r.messageCount },
            { key: 'u', header: t('audit-remediation.expert.admin.threads.updated'), narrow: 'hide', render: (r) => <When iso={r.updatedAt} /> },
          ]} />
        )}
      </Loadable>
    </>
  );
}

export function ExpertThreadPage() {
  const { t, roles } = useSurface();
  const api = useApi();
  const { tenantId = '', threadId = '' } = useParams();
  const data = useResource(threadKey(tenantId, threadId), () => api.thread(tenantId, threadId));
  const [body, setBody] = useState('');
  const send = useAction(() => api.reply(tenantId, threadId, body.trim()));
  return (
    <Screen title={t('audit-remediation.expert.admin.threads.one')} testId="audit-expert-thread">
      <Loadable resource={data}>
        {(d) => (
          <>
            <Text dir="auto">{d.thread.title}</Text>
            <ol className="qa-expert__thread">
              {d.messages.map((m) => (
                <li key={m.id} data-role={m.role}>
                  <Badge tone={m.role === 'expert' ? 'accent' : 'neutral'}>{t(`audit-remediation.advisor.role.${m.role}`)}</Badge> <When iso={m.createdAt} />
                  <Text dir="auto">{m.body}</Text>
                  {m.flags.length ? <Text tone="muted">{m.flags.map((f) => t(`audit-remediation.expert.admin.flag.${f}`)).join(' · ')}</Text> : null}
                </li>
              ))}
            </ol>
            {canWriteNotes(roles) ? (
              <form className="qa-expert__form" onSubmit={async (e) => { e.preventDefault(); if (body.trim() && (await send.run())) { setBody(''); data.reload(); } }}>
                <Field label={t('audit-remediation.expert.admin.threads.reply')} hint={t('audit-remediation.expert.admin.threads.replyHint')}>
                  {(id) => <textarea id={id} name="reply" rows={3} maxLength={4000} value={body} onChange={(e) => setBody(e.target.value)} />}
                </Field>
                <Button type="submit" variant="primary" disabled={!body.trim() || send.pending}>{t('audit-remediation.expert.admin.threads.send')}</Button>
                <ErrorNote error={send.error} />
              </form>
            ) : null}
          </>
        )}
      </Loadable>
    </Screen>
  );
}

const B_TABS: readonly (BookingStatus | 'all')[] = ['requested', 'payment_pending', 'confirmed', 'completed', 'cancelled', 'all'];

export function ExpertBookingsPage() {
  const { t } = useSurface();
  const api = useApi();
  const [tab, setTab] = useState<BookingStatus | 'all'>('requested');
  const list = useResource(bookingsKey(tab), () => api.bookings(tab === 'all' ? undefined : tab));
  const [open, setOpen] = useState<StaffBooking | null>(null);
  return (
    <Screen title={t('audit-remediation.expert.admin.bookings.title')} subtitle={t('audit-remediation.expert.admin.bookings.subtitle')} testId="audit-expert-bookings"
      actions={<Link to="/audit-expert">{t('audit-remediation.expert.admin.back')}</Link>}>
      <Tabs tabs={B_TABS.map((id) => ({ id, label: t(id === 'all' ? 'audit-remediation.expert.admin.queue.tab.all' : `audit-remediation.expert.bookingStatus.${id}`) }))} active={tab}
        onSelect={(id) => setTab(id as BookingStatus | 'all')} label={t('audit-remediation.expert.admin.bookings.tabsLabel')}>
        <Loadable resource={list}>
          {(rows) => (
            <DataTable rows={rows} rowKey={(r) => r.id} onRowClick={setOpen} empty={t('audit-remediation.expert.admin.bookings.empty')} columns={[
              { key: 'topic', header: t('audit-remediation.expert.admin.bookings.topic'), render: (r) => <span dir="auto">{r.topic}</span> },
              { key: 'tenant', header: t('audit-remediation.expert.admin.bookings.tenant'), narrow: 'hide', render: (r) => <code dir="ltr">{r.tenantId}</code> },
              { key: 'when', header: t('audit-remediation.expert.admin.bookings.when'), render: (r) => (r.scheduledAt ? <When iso={r.scheduledAt} /> : t('audit-remediation.expert.admin.bookings.slotsCount', { n: r.preferredSlots.length })) },
              { key: 'price', header: t('audit-remediation.expert.admin.bookings.price'), narrow: 'hide', render: (r) => <Money minor={r.priceMinor} currency={r.currency} /> },
              { key: 's', header: t('audit-remediation.expert.admin.col.status'), render: (r) => <Badge tone={r.status === 'confirmed' ? 'good' : r.status === 'cancelled' ? 'neutral' : 'accent'}>{t(`audit-remediation.expert.bookingStatus.${r.status}`)}</Badge> },
            ]} />
          )}
        </Loadable>
      </Tabs>
      {open ? <BookingBlade booking={open} onClose={() => setOpen(null)} onDone={() => { setOpen(null); list.reload(); }} /> : null}
    </Screen>
  );
}

function BookingBlade({ booking, onClose, onDone }: { booking: StaffBooking; onClose: () => void; onDone: () => void }) {
  const { t, roles } = useSurface();
  const api = useApi();
  const [expertId, setExpertId] = useState(booking.expertId ?? '');
  const [slot, setSlot] = useState(booking.preferredSlots[0] ?? '');
  const act = useAction((b: Parameters<typeof api.bookingAction>[2]) => api.bookingAction(booking.tenantId, booking.id, b));
  const actions = bookingActionsFor(booking.status, roles);
  const run = async (b: Parameters<typeof api.bookingAction>[2]) => { if (await act.run(b)) onDone(); };
  return (
    <Blade title={booking.topic} onClose={onClose} testId="booking-blade">
      <Text>{t(`audit-remediation.expert.bookingStatus.${booking.status}`)} · {t('audit-remediation.expert.book.minutes', { n: booking.durationMin })}</Text>
      {booking.scheduledAt ? <Text><When iso={booking.scheduledAt} /></Text> : null}
      {actions.includes('assign') || actions.includes('confirm') ? (
        <Field label={t('audit-remediation.expert.admin.bookings.expert')}>
          {(id) => <input id={id} name="expertId" maxLength={80} value={expertId} onChange={(e) => setExpertId(e.target.value)} />}
        </Field>
      ) : null}
      {actions.includes('assign') ? <Button disabled={!expertId.trim() || act.pending} onClick={() => run({ action: 'assign', expertId: expertId.trim() })}>{t('audit-remediation.expert.admin.bookings.assign')}</Button> : null}
      {actions.includes('confirm') ? (
        <>
          <Field label={t('audit-remediation.expert.admin.bookings.slot')} hint={t('audit-remediation.expert.admin.bookings.slotHint')}>
            {(id) => (
              <select id={id} name="slot" value={slot} onChange={(e) => setSlot(e.target.value)}>
                {booking.preferredSlots.map((s) => <option key={s} value={s}>{new Date(s).toISOString().replace('T', ' ').slice(0, 16)} UTC</option>)}
              </select>
            )}
          </Field>
          <Button variant="primary" disabled={!slot || act.pending} onClick={() => run({ action: 'confirm', scheduledAt: slot, ...(expertId.trim() ? { expertId: expertId.trim() } : {}) })}>{t('audit-remediation.expert.admin.bookings.confirm')}</Button>
        </>
      ) : null}
      {actions.includes('complete') ? <Button disabled={act.pending} onClick={() => run({ action: 'complete' })}>{t('audit-remediation.expert.admin.bookings.complete')}</Button> : null}
      {actions.includes('waive_payment') ? <ReasonForm label={t('audit-remediation.expert.admin.bookings.waiveReason')} submitLabel={t('audit-remediation.expert.admin.bookings.waive')} pending={act.pending} onSubmit={(reason) => run({ action: 'waive_payment', reason })} /> : null}
      {actions.includes('cancel') ? <ReasonForm danger label={t('audit-remediation.expert.admin.bookings.cancelReason')} submitLabel={t('audit-remediation.expert.admin.bookings.cancel')} pending={act.pending} onSubmit={(reason) => run({ action: 'cancel', reason })} /> : null}
      {!actions.length ? <Text tone="muted">{t('audit-remediation.expert.admin.bookings.noActions')}</Text> : null}
      <ErrorNote error={act.error} />
    </Blade>
  );
}
